import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  useAchievements,
  rangeStartDate,
  rangeEndDate,
  type CustomRangeBounds,
  type RangeKey,
} from '@busy/api';
import { Panel } from '@busy/ui';
import { useT } from '../../lib/i18n';
import { ACHIEVEMENT_CATALOG, byEarnedDesc, thresholdLabel } from '../../lib/achievements';
import { KindIcon } from '../CelebrationModal';

// "Recent achievements" tile on the Dashboard. Shows up to 4 most-
// recently earned milestones as small chips, scoped to the Dashboard's
// active range selector — pass `range` so the tile matches the same
// window the rest of the metrics use. Without a range, the tile falls
// back to "everything ever" so the component stays usable in any
// future surface (e.g. /achievements has its own empty state already).
//
// The whole tile + the View all chip route to /achievements.
export function RecentAchievementsTile({
  range,
  customBounds,
}: {
  range?: RangeKey;
  customBounds?: CustomRangeBounds | null;
} = {}) {
  const t = useT();
  const all = useAchievements();
  // Filter by earned_at >= rangeStart when a range is provided. The
  // sort + slice still cap the visible chip set at 4 — the tile is a
  // glance, not a full archive. Custom ranges also apply an upper bound.
  const recent = useMemo(() => {
    const rows = all.data ?? [];
    if (!range) return byEarnedDesc(rows).slice(0, 4);
    const startMs = rangeStartDate(range, customBounds).getTime();
    const inRange =
      range === 'custom'
        ? (() => {
            const endMs = rangeEndDate(range, customBounds).getTime();
            return rows.filter((a) => {
              const ts = new Date(a.earned_at).getTime();
              return ts >= startMs && ts <= endMs;
            });
          })()
        : rows.filter((a) => new Date(a.earned_at).getTime() >= startMs);
    return byEarnedDesc(inRange).slice(0, 4);
  }, [all.data, range, customBounds?.start, customBounds?.end]);

  return (
    <Panel
      title={t('ach.tile.title')}
      sub={t('ach.tile.sub')}
      action={
        <Link
          to="/achievements"
          className="text-[11px] tracking-[0.14em] uppercase font-extrabold text-navy-900 dark:text-gold-500 hover:underline"
        >
          {t('ach.tile.viewAll')}
        </Link>
      }
    >
      {recent.length === 0 ? (
        <p className="text-sm text-ink-500 text-center py-4">{t('ach.tile.empty')}</p>
      ) : (
        <Link to="/achievements" className="block">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
            {recent.map((a) => {
              const catalog = ACHIEVEMENT_CATALOG[a.kind];
              const thr = thresholdLabel(a.kind, a.threshold);
              return (
                <div
                  key={a.id}
                  className={`${catalog.gradient} text-white rounded-xl p-3 flex items-center gap-2.5 shadow-sm`}
                >
                  <div
                    className={`w-9 h-9 rounded-lg bg-white/15 grid place-items-center shrink-0 ${catalog.iconColor}`}
                  >
                    <KindIcon kind={a.kind} className="w-4.5 h-4.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[11px] tracking-[0.1em] uppercase font-bold text-white/75">
                      {t(`ach.kind.${a.kind}`)}
                    </div>
                    <div className="font-bold text-[13px] leading-tight truncate">
                      {t(thr.key, thr.vars)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Link>
      )}
    </Panel>
  );
}
